import {ReactElement, useContext, useEffect, useMemo, useState} from "react";
import {useNavigate} from "react-router-dom";
import {useQuery} from "@apollo/client";

import {Button, Text} from "../../components/input";
import {Collection, CREATE_COLLECTION, GET_COLLECTIONS, loadCollection} from "../../data";
import {CollectionItem} from "./collection-item";
import {useDebounce} from "../../debounce";
import {defaultLoadingProps as loadingProps, usePaginatedQuery} from "../../paginated-query";
import {useMutationToast} from "../../toast";
import {CollectionContext} from "./index";
import {usePageInput, usePageScroll} from "../../layout";

import styles from "./collection.module.scss";

const empty = (
	<div className={styles.empty}>
		<img src="/svgs/collection_blank.svg" alt="No Collections" />
	</div>
);
const renderItem = collection => <CollectionItem key={collection.id} collection={collection} />;

export const Collections = (): ReactElement => {
	const [search, setSearch] = useState("");
	const {filter} = useContext(CollectionContext);
	const debouncedSearch = useDebounce(search, 300);

	usePageInput(() => <Text value={search} onChange={setSearch} placeholder="Search" icon="search" />, [
		search,
	]);

	const navigate = useNavigate();
	const [createCollection, {loading: creating}] = useMutationToast(CREATE_COLLECTION, {
		onCompleted: ({createCollection: {id}}) => navigate(`/collections/${id}`),
	});

	usePageInput(() => <Button loading={creating} onClick={createCollection} value="Create Collection" />, [
		createCollection,
		creating,
	]);
	const variables = useMemo(
		() => ({
			limit: 20,
			sort: filter.sort?.toUpperCase(),
			status: filter.status?.map(s => s.toUpperCase()),
			groups: filter.groups.length ? filter.groups : undefined,
			owner: filter.owner,
			search: debouncedSearch,
		}),
		[filter, debouncedSearch]
	);

	const {handleScroll, render, data} = usePaginatedQuery<Collection>(GET_COLLECTIONS, {
		loadingProps,
		inflateItem: loadCollection,
		variables,
		renderItem,
		itemsClassName: styles.collectionList,
		empty,
	});
	usePageScroll(handleScroll);
	const toBeSentCollections = useMemo(
		() =>
			data
				.filter(
					collection =>
						collection.status === "approved" &&
						!collection.sent &&
						collection.scheduledFor &&
						collection.scheduledFor?.isBefore()
				)
				.map(collection => collection.id),
		[data]
	);

	const {startPolling, stopPolling} = useQuery(GET_COLLECTIONS, {
		variables: {
			ids: toBeSentCollections,
		},
		skip: toBeSentCollections.length === 0,
	});

	useEffect(() => {
		if (toBeSentCollections.length === 0) return stopPolling();
		startPolling(5000);
	}, [startPolling, stopPolling, toBeSentCollections]);

	return render;
};
